/**
 * Passport Eligibility Section
 * Single-form layout (one row per user). Explicit Save button.
 * No sponsorship question.
 */

import { useState, useEffect } from 'react';
import { PassportSectionFallback } from './PassportSectionFallback';
import { PassportProvenanceBadge } from './PassportProvenanceBadge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { ClipboardCheck, Loader2, Check } from 'lucide-react';
import { usePassportEligibility, useUpsertEligibility } from '@/hooks/usePassportSections';
import { clock } from '@/utils/clock';
import {
  TIME_SENSITIVE_ELIGIBILITY_FIELDS,
  describeStartDateForViewer,
  describeStatedOn,
  localTodayIso,
  startDateStanding,
} from '@/lib/passport/eligibilityTimeSensitivity';

interface Props {
  userId: string;
  isOwner: boolean;
  isReadOnly?: boolean;
}

interface FormData {
  has_valid_drivers_license: boolean | null;
  authorized_to_work_us: boolean | null;
  available_start_date: string;
  willing_background_check: boolean | null;
  willing_drug_screen: boolean | null;
  willing_relocate: boolean | null;
}

const TOGGLE_FIELDS: { key: keyof Omit<FormData, 'available_start_date'>; label: string }[] = [
  { key: 'has_valid_drivers_license', label: 'Valid driver\'s license' },
  { key: 'authorized_to_work_us', label: 'Authorized to work in the US' },
  { key: 'willing_background_check', label: 'Willing to complete a background check' },
  { key: 'willing_drug_screen', label: 'Willing to complete a drug screen' },
  { key: 'willing_relocate', label: 'Willing to relocate' },
];

export function PassportEligibilitySection({ userId, isOwner, isReadOnly }: Props) {
  // FIX-R37-MED-4: pass isOwner so non-owners get the public-safe column
  // subset only (no work_authorized, drivers_license, background_check,
  // drug_screen). Defense-in-depth ahead of the Phase 2 RLS tightening.
  const { data: existing, isLoading, isError } = usePassportEligibility(userId, { isOwner });
  const upsert = useUpsertEligibility();

  const [form, setForm] = useState<FormData>({
    has_valid_drivers_license: null,
    authorized_to_work_us: null,
    available_start_date: '',
    willing_background_check: null,
    willing_drug_screen: null,
    willing_relocate: null,
  });
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (existing) {
      setForm({
        has_valid_drivers_license: existing.has_valid_drivers_license,
        authorized_to_work_us: existing.authorized_to_work_us,
        available_start_date: existing.available_start_date || '',
        willing_background_check: existing.willing_background_check,
        willing_drug_screen: existing.willing_drug_screen,
        willing_relocate: existing.willing_relocate,
      });
      setDirty(false);
    }
  }, [existing]);

  const update = (key: string, val: any) => {
    setForm(prev => ({ ...prev, [key]: val }));
    setDirty(true);
  };

  const handleSave = () => {
    upsert.mutate({
      user_id: userId,
      ...form,
      available_start_date: form.available_start_date || null,
    }, { onSuccess: () => setDirty(false) });
  };

  // Core-100 R4 — CORE-N time-sensitive answers: shown with the date they were stated; a passed start date is not
  // current availability. The viewer's LOCAL date decides "passed".
  const todayIso = localTodayIso(clock.now());
  const statedOn = describeStatedOn(existing?.updated_at ?? null);
  const startPassed = startDateStanding(form.available_start_date, todayIso) === 'passed';
  const isTimeSensitive = (key: string) => (TIME_SENSITIVE_ELIGIBILITY_FIELDS as readonly string[]).includes(key);

  const renderToggle = (key: keyof Omit<FormData, 'available_start_date'>, label: string) => {
    const val = form[key] as boolean | null;
    if (!isOwner || isReadOnly) {
      return (
        <div key={key} className="flex items-center justify-between py-1.5">
          <span className="text-sm">{label}</span>
          <span className="text-sm text-muted-foreground">
            {val === true ? 'Yes' : val === false ? 'No' : '—'}
            {val !== null && isTimeSensitive(key) && statedOn ? (
              <span className="ml-1 text-xs" data-testid={`eligibility-stated-${key}`}>({statedOn})</span>
            ) : null}
          </span>
        </div>
      );
    }
    // Unknown is not false: an unanswered question is said as unanswered, and an answer can be withdrawn back to it.
    return (
      <div key={key} className="flex items-center justify-between py-1.5">
        <Label className="font-normal">{label}</Label>
        <span className="flex items-center gap-2">
          {val === null ? (
            <span className="text-xs text-muted-foreground" data-testid={`eligibility-unanswered-${key}`}>Not answered</span>
          ) : (
            <Button type="button" variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => update(key, null)}>
              Clear
            </Button>
          )}
          <Switch
            aria-label={label}
            checked={val === true}
            onCheckedChange={(c) => update(key, c)}
          />
        </span>
      </div>
    );
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <ClipboardCheck className="h-5 w-5 text-primary" />
          <CardTitle className="text-lg">Eligibility</CardTitle>
          {/* CORE-C GAP-002. Every answer in this card is the person's own
              statement - "authorised to work" is what they said, not a legal
              determination - so one badge covers a homogeneous group. */}
          <PassportProvenanceBadge factGroupId="eligibility" />
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {/* CORE-C GAP-003: on a failed read the toggles below would render
            their DEFAULTS, presenting unanswered questions as the person's
            stated eligibility. Worse, an owner could then save those
            defaults over real answers. The form is withheld instead. */}
        {isLoading ? (
          <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : isError ? (
          <PassportSectionFallback sectionName="Eligibility" inline />
        ) : (
          <>
            {TOGGLE_FIELDS.map(({ key, label }) => renderToggle(key, label))}

            {/* Available start date */}
            <div className="flex items-center justify-between py-1.5">
              {isOwner && !isReadOnly ? (
                <>
                  <Label className="font-normal">Available start date</Label>
                  <Input
                    type="date"
                    className="w-40"
                    value={form.available_start_date}
                    onChange={(e) => update('available_start_date', e.target.value)}
                  />
                </>
              ) : (
                <>
                  <span className="text-sm">Available start date</span>
                  <span className="text-sm text-muted-foreground" data-testid="eligibility-start-date">
                    {describeStartDateForViewer(form.available_start_date, todayIso)}
                  </span>
                </>
              )}
            </div>
            {isOwner && !isReadOnly && startPassed && (
              <p className="text-xs text-amber-600" data-testid="eligibility-start-date-passed">
                Your available start date has passed. Shops will not see it as current until you update it.
              </p>
            )}
            {statedOn && (
              <p className="text-xs text-muted-foreground" data-testid="eligibility-as-of">
                Availability and willingness change: these answers were {statedOn.toLowerCase()}.
              </p>
            )}

            {isOwner && !isReadOnly && (
              <div className="pt-2">
                <Button
                  onClick={handleSave}
                  disabled={!dirty || upsert.isPending}
                  size="sm"
                  className="w-full"
                >
                  {upsert.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-1" />
                  ) : dirty ? null : (
                    <Check className="h-4 w-4 mr-1" />
                  )}
                  {dirty ? 'Save Eligibility' : 'Saved'}
                </Button>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
